import React, { createContext, useState, useContext, useCallback } from 'react';
import languages from '../i18n';

const LanguageContext = createContext(null);

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)', short: 'ET' },
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)', short: 'CT' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)', short: 'MT' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)', short: 'PT' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time (BRT)', short: 'BRT' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time (ART)', short: 'ART' },
  { value: 'Europe/London', label: 'London (GMT/BST)', short: 'GMT' },
  { value: 'Europe/Paris', label: 'Central Europe (CET/CEST)', short: 'CET' },
  { value: 'Europe/Lisbon', label: 'Lisbon (WET/WEST)', short: 'WET' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)', short: 'JST' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', short: 'AEST' },
  { value: 'UTC', label: 'UTC', short: 'UTC' },
];

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('cardlockr_lang') || 'en';
  });

  const [timezone, setTimezoneState] = useState(() => {
    return localStorage.getItem('cardlockr_timezone') || 'America/New_York';
  });

  const t = languages[lang]?.translations || languages.en.translations;

  const setLanguage = useCallback((newLang) => {
    if (languages[newLang]) {
      setLang(newLang);
      localStorage.setItem('cardlockr_lang', newLang);
    }
  }, []);

  const setTimezone = useCallback((tz) => {
    setTimezoneState(tz);
    localStorage.setItem('cardlockr_timezone', tz);
  }, []);

  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat(t.currencyLocale, {
      style: 'currency',
      currency: t.currency,
    }).format(value);
  }, [t.currencyLocale, t.currency]);

  const formatDate = useCallback((d) => {
    return t.dateFormat.formatFn(d, timezone);
  }, [t, timezone]);

  const formatDateTime = useCallback((d) => {
    return t.dateFormat.formatTimeFn(d, timezone);
  }, [t, timezone]);

  return (
    <LanguageContext.Provider value={{
      lang,
      setLanguage,
      t,
      languages,
      formatCurrency,
      formatDate,
      formatDateTime,
      timezone,
      setTimezone,
      TIMEZONE_OPTIONS,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
